import { useState, useEffect } from 'react';
import { Search, User, ArrowLeftIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from "../lib/api";

export default function SearchPage() {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // 🔁 Debounced search effect
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (search.trim()) {
        fetchResults(search);
      } else {
        setResults([]);
      }
    }, 400); // debounce delay

    return () => clearTimeout(delayDebounce);
  }, [search]);

  const fetchResults = async (query) => {
    try {
      setLoading(true);
      const res = await api.get(`/api/doctor/search?query=${encodeURIComponent(query)}`);
      setResults(res.data.results || []);
    } catch (err) {
      console.error("❌ Failed to fetch doctors:", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <ArrowLeftIcon onClick={() => navigate(-1)} role="button" />

      {/* Search Bar */}
      <div className="input-group shadow-sm mb-4">
        <span className="input-group-text bg-white border-end-0">
          <Search size={16} className="text-muted" />
        </span>
        <input
          type="text"
          className="form-control border-start-0"
          placeholder="Search for a doctor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ fontSize: 14 }}
        />
      </div>

      {/* Recent Searches */}
      {!search && (
        <>
          <h6 className="fw-semibold mb-2">Recent Searches</h6>
          <div className="d-flex flex-wrap gap-2 mb-4">
            {['Dr. Hannibal Lecter', 'Dr. Kenzo Tenma', 'Dr. Luigi Black'].map((name, idx) => (
              <button
                key={idx}
                className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                onClick={() => setSearch(name)}
              >
                {name}
              </button>
            ))}
          </div>
        </>
      )}

      {/* No Results */}
      {search && !loading && results.length === 0 && (
        <div className="text-center py-5">
          <Search size={120} color='var(--accent)'/>
          <p className="text-muted mt-3">
            We couldn’t find results for <strong>{search}</strong>.
          </p>
          <p className="text-muted">Please enter another keyword or browse categories below.</p>
          <div className="d-flex justify-content-center gap-2 mt-3">
            {['Orthopedics', 'Pediatrics'].map((cat, idx) => (
              <button key={idx} className="btn btn-outline-primary btn-sm rounded-pill px-3">
                {cat}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-semibold mb-0">All Results ({results.length})</h6>
            <select className="form-select form-select-sm w-auto">
              <option>Most Relevant</option>
              <option>Highest Rated</option>
              <option>Earliest Availability</option>
            </select>
          </div>

          {results.map((doc, idx) => (
            <div key={idx} className="card shadow-sm mb-3 p-3 rounded-4">
              <h6 onClick={() => navigate(`/doctor-details/${doc._id}`)} className="fw-semibold mb-1">{doc.name}</h6>
              <p className="text-muted mb-1" style={{ fontSize: 13 }}>
                <User size={13} className="me-1" />
                {doc.specialty} • {doc.rating}★ ({doc.reviews} reviews)
              </p>
              <p className="text-success mb-1" style={{ fontSize: 13 }}>
                {doc.availability}
              </p>
              <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                Next available: {doc.nextAvailable}
              </p>
              <button
                className="btn btn-accent btn-sm w-100"
                onClick={() => navigate('/booking-screen', {state : doc})}
              >
                Book Now
              </button>
            </div>
          ))}
        </>
      )}
    </div>
  );
}